import { getApiClient } from '../api_client/get';
import { Container } from 'unstated';
import { getPersistentStore, getPersistentStoreMutex } from '@lemonadefashion/storage';
import FavoriteElement from '../models/favorite_element';

const KEY = 'favorites_v1';
function _getStore() { return getPersistentStore(); }
function _getMutex() { return getPersistentStoreMutex(); }

// This function is guaranteed not to acquire any locks or do any writes.
function getFavoritesFromStore() {
  try {
    const favoritesJson = _getStore().getItem(KEY);
    if (favoritesJson == null) return [];
    return JSON.parse(favoritesJson).map(info => new FavoriteElement(info));
  } catch (error) {
    console.log(error);
    return [];
  }
}

function setFavoritesInStore(favoritesInfoList) {
  return new Promise((resolve, reject) => {
    const mutex = _getMutex();
    const store = _getStore();
    mutex.lock(KEY)
      .then(() => {
        store.setItem(KEY, JSON.stringify(favoritesInfoList));
        return mutex.release(KEY).then(resolve).catch(reject);
      })
      .catch(reject);
  });
}

class FavoritesContainer extends Container {
  constructor() {
    super();
    this.state = this._getUpdateFromStore();
    // This will ensure state is synchronized with persistent store.
    // This event would only be fired if we're using localStorage internally.
    window.addEventListener('storage', (e) => {
      if (e.key !== KEY) return;
      this.setState(this._getUpdateFromStore());
    });
  }

  initialize() {
    if (this.shouldGoToNetwork()) {
      return this.loadFromNetwork();
    } else {
      return Promise.resolve();
    }
  }

  shouldGoToNetwork() {
    return true;
  }

  _getUpdateFromStore() {
    return { favorites: getFavoritesFromStore() };
  }

  _networkResolve(resolve, response) {
    if (response.data.favorites) {
      // We should only resolve it when we have network-storage-state consistency.
      // If writing to persistent storage fails, we should still update memory-state.
      setFavoritesInStore(response.data.favorites)
        .catch(console.log)
        .finally(() => this.setState(this._getUpdateFromStore(), () => resolve(response)));
    } else {
      resolve(response);
    }
  }

  forceRefresh() {
    return this.loadFromNetwork();
  }

  loadFromNetwork() {
    return new Promise((resolve, reject) => {
      getApiClient().getFavorites()
        .then(this._networkResolve.bind(this, resolve))
        .catch(reject);
    });
  }

  all() { return this.state.favorites; }
  size() { return this.state.favorites.length; }

  get(index) {
    if (index < 0 || index >= this.state.favorites.length) throw new Error('Favorites index out of bounds.');
    return this.state.favorites[index];
  }

  add(itemId) {
    return new Promise((resolve, reject) => {
      getApiClient().addToFavorites(itemId)
        .then(this._networkResolve.bind(this, resolve))
        .catch(reject);
    });
  }

  getFavoriteElementIdByItemId(itemId) {
    for (let i = 0; i < this.state.favorites.length; ++i) {
      if (this.state.favorites[i].getItemId() === itemId) return this.state.favorites[i].getId();
    }
    return null;
  }

  setFavorites(favoritesInfoList) {
    return new Promise((resolve, reject) => {
      setFavoritesInStore(favoritesInfoList)
        .catch(console.log)
        .finally(() => this.setState(this._getUpdateFromStore(), resolve));
    });
  }

  getElementById(favoriteElementId) {
    const result = this.state.favorites.find(element => element.getId() === favoriteElementId);
    return result || null;
  }

  remove(favoriteElementId) {
    return new Promise((resolve, reject) => {
      getApiClient().removeFromFavorites(favoriteElementId)
        .then(this._networkResolve.bind(this, resolve))
        .catch(reject);
    });
  }
}

export default FavoritesContainer;
